import { useRef, useState, useMemo } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  AreaChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import type { TooltipContentProps } from 'recharts'
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent'
import { BarChart2, TrendingUp, AreaChart as AreaChartIcon, Download } from 'lucide-react'
import html2canvas from 'html2canvas'
import { ReportDataRow, ReportBreakdown } from '../../../types/reports'
import {
  DERIVED_METRICS,
  formatMetricLabel,
  getMetricColor,
  getMetricValue,
  isDerivedMetric,
} from '../metricsConfig'

interface Props {
  data: ReportDataRow[]
  breakdowns: ReportBreakdown[]
  selectedMetrics: string[]
}

type ChartType = 'bar' | 'line' | 'area'

const DIMENSION_LABELS: Partial<Record<ReportBreakdown, string>> = {
  campaigns: 'Campaign',
  ad_units: 'Ad Unit',
  ads: 'Ad',
  date: 'Date',
  hour: 'Hour',
  country: 'Country',
  domain: 'Domain',
  content_type: 'Content Type',
  size: 'Size',
}

const CHART_TYPES: { value: ChartType; label: string; icon: React.ReactNode }[] = [
  { value: 'bar', label: 'Bar', icon: <BarChart2 className="w-4 h-4" /> },
  { value: 'line', label: 'Line', icon: <TrendingUp className="w-4 h-4" /> },
  { value: 'area', label: 'Area', icon: <AreaChartIcon className="w-4 h-4" /> },
]

function resolveLabel(row: ReportDataRow, breakdown: ReportBreakdown): string {
  switch (breakdown) {
    case 'campaigns':
      return String(row.campaign_name ?? (row.campaign_id ? `ID ${row.campaign_id}` : '?'))
    case 'ad_units':
      return String(row.ad_unit_name ?? (row.ad_unit_id ? `ID ${row.ad_unit_id}` : '?'))
    case 'ads':
      return String(row.ad_name ?? (row.ad_id ? `ID ${row.ad_id}` : '?'))
    case 'date':
      return String(row.date ?? '?')
    case 'hour':
      return row.hour !== undefined ? `${String(row.hour).padStart(2, '0')}:00` : '?'
    case 'country':
      return String(row.country_name ?? (row.country !== undefined ? String(row.country) : '?'))
    case 'domain':
      return String(row.domain_name ?? (row.domain !== undefined ? String(row.domain) : '?'))
    case 'content_type':
      return String(row.content_type ?? '?')
    case 'size':
      return String(row.size ?? '?')
    default:
      return '?'
  }
}

function fmtAxisNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return String(n)
}

function CustomTooltip({ active, payload, label }: TooltipContentProps<ValueType, NameType>) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg px-4 py-3 text-sm min-w-[160px]">
      <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2 truncate max-w-[200px]">{String(label)}</p>
      {payload.map((entry, i) => {
        const derived = DERIVED_METRICS.find((d) => d.key === entry.dataKey)
        const formatted =
          typeof entry.value === 'number'
            ? derived?.isPercentage
              ? `${entry.value.toFixed(2)}%`
              : entry.value.toLocaleString()
            : String(entry.value ?? '')
        return (
          <div key={i} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.color }} />
              {entry.name}
            </span>
            <span className="font-medium text-gray-900 dark:text-white tabular-nums">{formatted}</span>
          </div>
        )
      })}
    </div>
  )
}

export function ReportChart({ data, breakdowns, selectedMetrics }: Props) {
  const [chartType, setChartType] = useState<ChartType>('bar')
  const [xBreakdown, setXBreakdown] = useState<ReportBreakdown>(breakdowns[0] ?? 'campaigns')
  const chartRef = useRef<HTMLDivElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const effectiveX: ReportBreakdown = breakdowns.includes(xBreakdown) ? xBreakdown : (breakdowns[0] ?? 'campaigns')

  // Split selected metrics into raw event types vs derived
  const rawMetricKeys = selectedMetrics.filter((m) => !isDerivedMetric(m))
  const activeDerived = DERIVED_METRICS.filter((d) => selectedMetrics.includes(d.key))

  const chartData = useMemo(() => {
    const grouped = new Map<string, Record<string, number> & { _sortKey: string | number }>()

    for (const row of data) {
      const label = resolveLabel(row, effectiveX)
      const sortKey =
        effectiveX === 'date' ? (String(row.date ?? '')) :
        effectiveX === 'hour' ? (Number.isFinite(Number(row.hour)) ? Number(row.hour) : 0) :
        label

      if (!grouped.has(label)) {
        const entry: Record<string, number | string> = { _sortKey: sortKey }
        for (const key of rawMetricKeys) entry[key] = 0
        grouped.set(label, entry as Record<string, number> & { _sortKey: string | number })
      }

      const existing = grouped.get(label)!
      for (const key of rawMetricKeys) {
        existing[key] = ((existing[key] as number) ?? 0) + getMetricValue(row, key)
      }
    }

    const entries = Array.from(grouped.entries()).map(([name, vals]) => {
      const entry: Record<string, string | number> = { name }
      for (const key of rawMetricKeys) entry[key] = (vals[key] as number) ?? 0
      const numericVals: Record<string, number> = Object.fromEntries(
        Object.entries(vals).map(([k, v]) => [k, typeof v === 'number' ? v : 0])
      )
      for (const d of activeDerived) entry[d.key] = d.compute(numericVals)
      entry._sortKey = vals._sortKey as string | number
      return entry
    })

    if (effectiveX === 'date' || effectiveX === 'hour') {
      entries.sort((a, b) => {
        const ak = a._sortKey, bk = b._sortKey
        if (typeof ak === 'number' && typeof bk === 'number') return ak - bk
        return String(ak).localeCompare(String(bk))
      })
    }

    return entries.map(({ _sortKey: _sk, ...rest }) => rest)
  }, [data, effectiveX, rawMetricKeys, activeDerived])

  // Determine if all visible metrics are percentage-type (for Y-axis formatting)
  const allPercentage =
    selectedMetrics.length > 0 &&
    selectedMetrics.every((k) => DERIVED_METRICS.find((d) => d.key === k)?.isPercentage)

  async function handleDownload() {
    if (!chartRef.current) return
    setIsDownloading(true)
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      })
      const link = document.createElement('a')
      link.download = `chart_${effectiveX}_${new Date().toISOString().slice(0, 10)}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
    } finally {
      setIsDownloading(false)
    }
  }

  // Build the list of series to render in order
  const allSeriesKeys = [...rawMetricKeys, ...activeDerived.map((d) => d.key)]

  function renderSeries(type: 'bar' | 'line' | 'area') {
    return allSeriesKeys.map((key, i) => {
      const color = getMetricColor(key, i)
      const name = isDerivedMetric(key)
        ? (DERIVED_METRICS.find((d) => d.key === key)?.label ?? key)
        : formatMetricLabel(key)

      if (type === 'bar') {
        return <Bar key={key} dataKey={key} name={name} fill={color} radius={[3, 3, 0, 0]} maxBarSize={40} />
      }
      if (type === 'line') {
        return <Line key={key} type="monotone" dataKey={key} name={name} stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
      }
      return (
        <Area key={key} type="monotone" dataKey={key} name={name} stroke={color} fill={color} fillOpacity={0.12} strokeWidth={2} dot={false} />
      )
    })
  }

  function renderChart() {
    const commonProps = {
      data: chartData,
      margin: { top: 8, right: 16, left: 8, bottom: 8 },
    }
    const xAxis = (
      <XAxis
        dataKey="name"
        tick={{ fontSize: 11, fill: 'currentColor' }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
        tickFormatter={(v: string) => v.length > 14 ? `${v.slice(0, 13)}…` : v}
        className="text-gray-500 dark:text-gray-400"
      />
    )
    const yAxis = (
      <YAxis
        tick={{ fontSize: 11, fill: 'currentColor' }}
        tickLine={false}
        axisLine={false}
        tickFormatter={allPercentage ? (v: number) => `${v}%` : fmtAxisNum}
        width={48}
        className="text-gray-500 dark:text-gray-400"
      />
    )
    const grid = <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.08} vertical={false} />
    const tooltip = <Tooltip content={CustomTooltip} cursor={{ fill: 'currentColor', fillOpacity: 0.04 }} />
    const legend = <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />

    if (chartType === 'bar') {
      return <BarChart {...commonProps}>{grid}{xAxis}{yAxis}{tooltip}{legend}{renderSeries('bar')}</BarChart>
    }
    if (chartType === 'line') {
      return <LineChart {...commonProps}>{grid}{xAxis}{yAxis}{tooltip}{legend}{renderSeries('line')}</LineChart>
    }
    return <AreaChart {...commonProps}>{grid}{xAxis}{yAxis}{tooltip}{legend}{renderSeries('area')}</AreaChart>
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl mb-6 overflow-hidden">
      {/* Controls */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex flex-wrap items-center gap-3">
        {/* Chart type toggle */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          {CHART_TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => setChartType(t.value)}
              title={t.label}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                chartType === t.value
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {t.icon}
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        {/* X-axis dimension selector */}
        {breakdowns.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Group by</span>
            <select
              value={effectiveX}
              onChange={(e) => setXBreakdown(e.target.value as ReportBreakdown)}
              className="text-sm px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {breakdowns.map((b) => (
                <option key={b} value={b}>{DIMENSION_LABELS[b] ?? b}</option>
              ))}
            </select>
          </div>
        )}

        {/* Download */}
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors border border-gray-300 dark:border-gray-700 disabled:opacity-50 ml-auto"
        >
          {isDownloading ? (
            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">Save PNG</span>
        </button>
      </div>

      {/* Chart area */}
      <div ref={chartRef} className="px-4 py-4 bg-white dark:bg-gray-900">
        {chartData.length === 0 || allSeriesKeys.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
            No data to display
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
